import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppBar, Avatar, Box, Divider, IconButton, Menu, MenuItem, Toolbar, Tooltip, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, PanelLeftClose, ChevronDown, UserRound, Settings, LogOut, Search as SearchIcon } from 'lucide-react';
import Search from './HeaderContent/Search';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';
import { getCurrentUser, hasAnyPermission } from 'utils/permissions';
import { logout } from 'utils/loginRedirect';

const sections = {
  catalog: 'Inventory',
  'product-catalog': 'Product catalog',
  listing: 'Channel listings',
  integration: 'Integrations',
  orders: 'Orders',
  competitors: 'Competitors',
  'audit-logs': 'Audit logs',
  'users-management': 'Users & roles',
  settings: 'Settings',
  profile: 'Your profile',
  inventory: 'Inventory'
};
export default function Header() {
  const { pathname } = useLocation();
  const mobile = useMediaQuery('(max-width:1023px)');
  const { menuMaster } = useGetMenuMaster();
  const open = Boolean(menuMaster?.isDashboardDrawerOpened);
  const width = mobile ? 0 : open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;
  const user = getCurrentUser();
  const name = user?.fullName || 'Your account';
  const [anchor, setAnchor] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const section = sections[pathname.split('/')[1]] || 'Overview';
  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="inherit"
      className="xc-topbar"
      sx={{
        left: width,
        width: `calc(100% - ${width}px)`,
        bgcolor: '#ffffffed',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid #e6ebf2',
        zIndex: 1100,
        transition: 'left .2s ease, width .2s ease'
      }}
    >
      <a className="xc-skip-link" href="#dashboard-main">
        Skip to content
      </a>
      <Toolbar sx={{ minHeight: '76px !important', gap: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}>
        <Tooltip title={open && !mobile ? 'Collapse navigation' : 'Open navigation'}>
          <IconButton
            onClick={() => handlerDrawerOpen(!open)}
            aria-label={open && !mobile ? 'Collapse navigation' : 'Open navigation'}
            sx={{ border: '1px solid #e6ebf2', borderRadius: 2, color: '#49617e' }}
          >
            {open && !mobile ? <PanelLeftClose size={19} /> : <MenuIcon size={19} />}
          </IconButton>
        </Tooltip>
        <div className="xc-topbar-context">
          <span>Workspace</span>
          <strong>{section}</strong>
        </div>
        <Box className="xc-header-search" sx={{ ml: 'auto', width: { sm: 250, lg: 340 }, display: { xs: 'none', sm: 'block' } }}>
          <Search />
        </Box>
        <IconButton
          aria-label="Search products and orders"
          onClick={() => setSearchOpen(!searchOpen)}
          sx={{ ml: 'auto', display: { sm: 'none' } }}
        >
          <SearchIcon size={20} />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ my: 2.5, display: { xs: 'none', sm: 'block' } }} />
        <button
          className="xc-profile-trigger"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          aria-controls={anchor ? 'xc-account-menu' : undefined}
        >
          <Avatar sx={{ width: 35, height: 35, bgcolor: '#e9f1ff', color: '#005ddc', fontSize: 13, fontWeight: 700 }}>
            {name
              .split(' ')
              .slice(0, 2)
              .map((word) => word[0])
              .join('')
              .toUpperCase()}
          </Avatar>
          <span className="xc-profile-text">
            <strong>{name}</strong>
            <span>{typeof user?.role === 'string' ? user.role : 'Your account'}</span>
          </span>
          <ChevronDown size={14} />
        </button>
        <Menu
          id="xc-account-menu"
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: { sx: { minWidth: 210, mt: 1.5, borderRadius: 3, border: '1px solid #e6ebf2', boxShadow: '0 12px 36px #183b681c' } }
          }}
        >
          <MenuItem component={Link} to="/profile" onClick={() => setAnchor(null)} sx={{ gap: 1.5 }}>
            <UserRound size={17} />
            My profile
          </MenuItem>
          {hasAnyPermission(['settings.view', 'settings.update']) && (
            <MenuItem component={Link} to="/settings" onClick={() => setAnchor(null)} sx={{ gap: 1.5 }}>
              <Settings size={17} />
              Settings
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={logout} sx={{ gap: 1.5, color: '#b63e4a' }}>
            <LogOut size={17} />
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
      {searchOpen && mobile && (
        <Box className="xc-header-search" sx={{ px: 2, pb: 2, display: { sm: 'none' } }}>
          <Search />
        </Box>
      )}
    </AppBar>
  );
}
